import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown, MoreVertical, Trash2, Image, Users, Check } from "lucide-react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { OrganisationUserTable } from "@/components/organisation-user-table";
import type { Organisation, organisationUsers } from "@shared/schema";

interface OrganisationPanelProps {
  org: Organisation & { organisationUsers: (typeof organisationUsers.$inferSelect)[] };
  isGod: boolean;
  isOwner?: boolean;
  onDeleteOrg: (orgId: number, orgName: string) => void;
  onAllocateUser: (orgId: number) => void;
  onRemoveUser: (orgId: number, userId: number, username: string) => void;
  onForceLogout: (userId: number) => void;
  onResetPassword: (userId: number, username: string) => void;
  onAddLogo: (orgId: number, orgName: string) => void;
  onToggleArtistManagement?: (orgId: number, enabled: boolean) => void;
}

export function OrganisationPanel({
  org,
  isGod,
  isOwner = false,
  onDeleteOrg,
  onAllocateUser,
  onRemoveUser,
  onForceLogout,
  onResetPassword,
  onAddLogo,
  onToggleArtistManagement,
}: OrganisationPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <GlassPanel>
      <div className="p-6 border-b border-white/20 relative z-10">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold text-slate-900/90 drop-shadow-sm">{org.name}</h3>
            <p className="text-slate-600/80 mt-1 drop-shadow-sm">Created at {new Date(org.createdAt).toLocaleDateString()}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-slate-600/80 hover:bg-white/20 hover:text-slate-700 transition-all duration-300"
              >
                <MoreVertical className="h-4 w-4 drop-shadow-sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onAddLogo(org.id, org.name)}>
                <Image className="mr-2 h-4 w-4" />
                <span>{org.logoUrl ? "Manage Logo" : "Add Logo"}</span>
              </DropdownMenuItem>
              {isGod && onToggleArtistManagement && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onToggleArtistManagement(org.id, !org.artistManagement)}
                    className="text-purple-600 focus:text-purple-700"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span className="flex-1">Artist Management</span>
                    {org.artistManagement && <Check className="h-4 w-4 ml-2" />}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDeleteOrg(org.id, org.name)}>
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete Organisation</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="p-6 relative z-10">
        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <div className="flex justify-between items-center">
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer rounded-md p-2 -ml-2">
                <h4 className="font-semibold text-lg text-slate-900/90 drop-shadow-sm">Users</h4>
                <ChevronDown className={`h-5 w-5 transition-transform duration-200 drop-shadow-sm text-slate-700/90 ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className="pt-4">
            <OrganisationUserTable
              users={org.organisationUsers}
              orgId={org.id}
              isGod={isGod}
              isOwner={isOwner}
              onAllocateUser={onAllocateUser}
              onRemoveUser={onRemoveUser}
              onForceLogout={onForceLogout}
              onResetPassword={onResetPassword}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </GlassPanel>
  );
}
